package com.hyperether.goodjob.scenes.pricing

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.Button
import androidx.compose.material.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.toSize
import androidx.navigation.NavHostController
import com.hyperether.goodjob.containerSize
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.models.Plan
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.navigation.CancelSubscriptionScreen
import com.hyperether.goodjob.navigation.Register
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.cancel_subscription
import com.hyperether.goodjob.resources.change_plan
import com.hyperether.goodjob.resources.choose_plan
import com.hyperether.goodjob.resources.choose_the_plan
import com.hyperether.goodjob.resources.choose_the_right_plan
import com.hyperether.goodjob.resources.error
import com.hyperether.goodjob.resources.from_small_teams
import com.hyperether.goodjob.resources.logo_dark_landing
import com.hyperether.goodjob.resources.logout
import com.hyperether.goodjob.resources.not_possible_to_buy_plan
import com.hyperether.goodjob.resources.subscription
import com.hyperether.goodjob.scenes.components.ErrorDialog
import com.hyperether.goodjob.scenes.components.MobileHeaderBackAndAction
import com.hyperether.goodjob.scenes.components.SubscriptionToggle
import com.hyperether.goodjob.scenes.components.disableMouseDragScrollOnWeb
import com.hyperether.goodjob.scenes.landing.WebContentHolder
import com.hyperether.goodjob.scenes.register.FilledButton
import com.hyperether.goodjob.scenes.register.FullScreenProgressBar
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.GrayButton
import com.hyperether.goodjob.theme.LandingPageBg
import com.hyperether.goodjob.theme.LandingPageMainTitle
import com.hyperether.goodjob.theme.LandingPageNavColor
import com.hyperether.goodjob.theme.LandingPageText
import com.hyperether.goodjob.util.WEB_SCREEN_SIZE_LIMIT
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.planner.util.verticalScrollbar
import com.hyperether.resources.currentLanguage
import com.hyperether.resources.stringResource
import org.jetbrains.compose.resources.painterResource

@Composable
fun PricingScreen(
    isMobileView: Boolean,
    pricingViewModel: PricingViewModel,
    navHostController: NavHostController,
    onChoosePlanClick: (Double, String) -> Unit
) {
    val plans = if (getPlatformChecker().isMobile()) {
        pricingViewModel.plans.collectAsState(initial = emptyList()).value
    } else {
        pricingViewModel.plansRemote.collectAsState(initial = emptyList()).value.sortedBy { it.price }
    }
    var existingPlan by remember { mutableStateOf<Plan?>(null) }
    val freebiePlan = plans.find {
        it.price == 0.0
    }
    val errorText = stringResource(Res.string.not_possible_to_buy_plan)

    LaunchedEffect(Unit) {
        if (!getPlatformChecker().isMobile()) {
            pricingViewModel.init()
        }
        pricingViewModel.getUser()?.let {
            it.pendingPlanId?.let { pendingPlanId -> pricingViewModel.getPlanById(pendingPlanId) }
        }
        pricingViewModel.selectedPlanId.value = ""
    }

    LaunchedEffect(plans, pricingViewModel.selectedPlanId.value) {
        existingPlan = plans.firstOrNull { it._id == pricingViewModel.getUser()?.planId }
            ?: pricingViewModel.getPlanFromWorkspace()
    }

    LaunchedEffect(plans) {
        if (plans.isNotEmpty()) {
            val popularPlan = plans.find { it.isPopular == true }
            pricingViewModel.selectedPlan.value =
                if (!pricingViewModel.isNavigationSourceDrawer.value) popularPlan?.getName(
                    currentLanguage.value.code
                )
                else existingPlan?.getName(currentLanguage.value.code)
            pricingViewModel.selectedPlanId.value =
                if (!pricingViewModel.isNavigationSourceDrawer.value) popularPlan?._id
                else existingPlan?._id

            pricingViewModel.showLoader.value = false
        }
    }

    LaunchedEffect(isMobileView) {
        pricingViewModel.getAllPlans()
    }

    LaunchedEffect(pricingViewModel.selectedOption.value, plans) {
        pricingViewModel.updateFilteredPlans()
    }

    if (isMobileView) {
        val isAdmin =
            pricingViewModel.getUser()?.role == Role.admin.name || pricingViewModel.getUser()?.role == Role.superAdmin.name
        // for the new user, there is no role, but type="user" in prefs
        val isPaymentEnabled =
            pricingViewModel.getUser()?.role != Role.employee.name && pricingViewModel.getUser()?.role != Role.manager.name
        Column {
            if (existingPlan != null) {
                MobileHeaderBackAndAction(
                    title = stringResource(Res.string.subscription),
                    back = { navHostController.navigateUp() },
                    actionEnabled = false
                )
            }
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .disableMouseDragScrollOnWeb()
                    .background(
                        brush = Brush.verticalGradient(
                            colors = listOf(
                                Color(0xFFe0f1ff),
                                Color(0xFFFFFFFF)
                            )
                        )
                    )
                    .padding(16.toScaleDp())
            ) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (existingPlan == null) {
                        Text(
                            text = stringResource(Res.string.choose_the_right_plan),
                            color = Color.Black,
                            fontSize = 28.toScaleSp(),
                            textAlign = TextAlign.Center,
                            fontWeight = FontWeight.Bold,
                            modifier = Modifier.padding(
                                bottom = 24.toScaleDp(),
                                top = 30.toScaleDp()
                            )
                        )
                    }

                    SubscriptionToggle(
                        isDark = false,
                        isMobile = true,
                        selectedOption = pricingViewModel.selectedOption.value,
                        onOptionSelected = {
                            pricingViewModel.selectedOption.value = it
                            pricingViewModel.updateFilteredPlans()
                            pricingViewModel.filteredPlans.value.forEach { plan ->
                                if (pricingViewModel.selectedPlanId.value == plan._id) {
                                    pricingViewModel.amount.value = plan.price
                                }
                            }
                        })
                    if (existingPlan != null)
                        Spacer(modifier = Modifier.height(15.toScaleDp()))
                    pricingViewModel.filteredPlans.value.forEach { plan ->
                        PricingItem(
                            isChecked = pricingViewModel.selectedPlanId.value == plan._id,
                            onClick = {
                                pricingViewModel.selectedPlan.value =
                                    plan.getName(currentLanguage.value.code)
                                pricingViewModel.selectedPlanId.value = plan._id
                                pricingViewModel.amount.value = plan.price
                                pricingViewModel.currency.value = plan.currency
                            },
                            isExistingPlan = existingPlan?._id == plan._id,
                            currentPlanExists = existingPlan != null,
                            plan = plan,
                            modifier = Modifier.padding(top = 10.toScaleDp()),
                            isAdmin = isAdmin
                        )
                    }
                    if (pricingViewModel.getUser()?.pendingPlanId != null) {
                        // TODO: check messageTextChangePlan.value
                        val downgradeText = pricingViewModel.getPlanChangeMessage()
                        androidx.compose.material.Text(
                            text = downgradeText,
                            fontSize = 12.toScaleSp(),
                            color = Color.Gray
                        )
                    }
                }
                if (isPaymentEnabled) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        modifier = Modifier
                            //.align(Alignment.BottomCenter)
                            .fillMaxWidth()
                            .padding(bottom = 10.toScaleDp())
                    ) {
                        FilledButton(
                            onClick = {
                                pricingViewModel.amount.value?.let { amount ->
                                    pricingViewModel.currency.value?.let { currency ->
                                        onChoosePlanClick(amount, currency)
                                    } ?: run {
                                        pricingViewModel.error.value =
                                            com.hyperether.goodjob.repository.remote.model.Resource.Error(
                                                errorText
                                            )
                                    }
                                } ?: run {
                                    pricingViewModel.error.value =
                                        com.hyperether.goodjob.repository.remote.model.Resource.Error(
                                            errorText
                                        )
                                }
                            },
                            modifier = Modifier.padding(top = 20.toScaleDp()),
                            text = if (existingPlan == null) stringResource(Res.string.choose_plan) else stringResource(
                                Res.string.change_plan
                            ),
                            isEnabled = existingPlan?._id != pricingViewModel.selectedPlanId.value,
                            color = if (existingPlan == null) BlueApp else {
                                if (existingPlan?._id == pricingViewModel.selectedPlanId.value) {
                                    GrayButton
                                } else {
                                    BlueApp
                                }
                            }
                        )
                        Spacer(modifier = Modifier.height(10.toScaleDp()))
                        if (existingPlan != null) {
                            if (pricingViewModel.selectedPlan.value != freebiePlan?.name) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(50.toScaleDp())
                                        .background(
                                            shape = RoundedCornerShape(25.toScaleDp()),
                                            color = Color.Transparent
                                        )
                                        .clickable {
                                            navHostController.navigate(CancelSubscriptionScreen)
                                        },
                                    contentAlignment = Alignment.Center
                                ) {
                                    DisableSelection {
                                        Text(
                                            text = stringResource(Res.string.cancel_subscription),
                                            color = Color.Gray
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    } else {
        // WEB
        val scrollState = rememberScrollState()
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScrollbar(scrollState)
                .verticalScroll(scrollState)
                .disableMouseDragScrollOnWeb()
                .background(LandingPageBg)
                .background(
                    Brush.radialGradient(
                        colors = listOf(
                            Color.White.copy(alpha = 0.2f),
                            Color.Transparent
                        ),
                        center = Offset(
                            x = 0f,
                            y = containerSize.value.height / 3f
                        ),
                        radius = containerSize.value.width / 4f
                    )
                )
                .onSizeChanged { size ->
                    containerSize.value = size.toSize()
                },
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Top
        ) {
            // header
            WebContentHolder(
                modifier = Modifier
                    .height(126.toScaleDp())
                    .background(LandingPageNavColor)
            ) {
                Row(
                    modifier = Modifier
                        .widthIn(max = WEB_SCREEN_SIZE_LIMIT)
                        .fillMaxWidth()
                        .fillMaxHeight(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Image(
                        alignment = Alignment.TopStart,
                        painter = painterResource(Res.drawable.logo_dark_landing),
                        contentDescription = null,
                        modifier = Modifier
                            .height(47.toScaleDp())
                            .width(203.toScaleDp())
                    )
                    Button(
                        onClick = {
                            pricingViewModel.logout()
                            navHostController.navigate(Register)
                        },
                        modifier = Modifier
                            .height(61.toScaleDp())
                            .width(208.toScaleDp())
                            .clip(RoundedCornerShape(50.toScaleDp()))
                            .pointerHoverIcon(PointerIcon.Hand),
                        colors = ButtonDefaults.buttonColors(Color.White),
                    ) {
                        Text(
                            text = stringResource(Res.string.logout),
                            color = BlueApp,
                            fontSize = 18.toScaleSp(),
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(60.toScaleDp()))
            // content
            WebContentHolder {
                Column(
                    modifier = Modifier
                        .widthIn(max = WEB_SCREEN_SIZE_LIMIT)
                        .fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center,
                ) {
                    Text(
                        text = stringResource(Res.string.choose_the_plan),
                        fontSize = 50.toScaleSp(),
                        lineHeight = 62.toScaleSp(),
                        color = LandingPageMainTitle,
                        fontWeight = FontWeight.W700,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.padding(vertical = 16.toScaleDp())
                    )
                    Spacer(modifier = Modifier.height(12.toScaleDp()))
                    Text(
                        text = stringResource(Res.string.from_small_teams),
                        fontSize = 18.toScaleSp(),
                        lineHeight = 30.toScaleSp(),
                        fontWeight = FontWeight.W400,
                        color = LandingPageText,
                        textAlign = TextAlign.Center
                    )

                    Spacer(modifier = Modifier.height(40.toScaleDp()))
                    SubscriptionToggle(
                        isDark = true,
                        isMobile = false,
                        selectedOption = pricingViewModel.selectedOption.value,
                        onOptionSelected = {
                            pricingViewModel.selectedOption.value = it
                            pricingViewModel.updateFilteredPlans()
                        })
                    Spacer(modifier = Modifier.height(80.toScaleDp()))

                    PricingPlansWeb(
                        isPricingScreen = true,
                        planList = pricingViewModel.filteredPlans.value,
                        onChoosePlan = { plan ->
                            pricingViewModel.onPlanChosen(
                                plan = plan,
                                errorText = errorText,
                                onChoosePlanClick = onChoosePlanClick
                            )
                        }
                    )
                }
            }
            Spacer(modifier = Modifier.height(90.toScaleDp()))
        }
    }

    pricingViewModel.error.value?.let {
        ErrorDialog(
            onDismiss = {
                pricingViewModel.error.value = null
            },
            title = stringResource(Res.string.error),
            message = it.text ?: ""
        )
    }
    if (pricingViewModel.showLoader.value) {
        FullScreenProgressBar()
    }
}